/**
 * Dashboard ejecutivo — solo rojos y amarillos del período actual.
 * Ordena por severidad (rojos primero), con comparativo vs período anterior
 * y sparkline de los últimos 6 períodos.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ArrowLeft, AlertTriangle, AlertOctagon, CheckCircle2 } from "lucide-react";
import {
  computeStatus,
  type KpiDefinition,
  type KpiValue,
  type SessionUser,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { EvaluationsTabs } from "@/features/evaluations/EvaluationsTabs";
import { api } from "@/lib/api";
import { canSeeDef } from "@/lib/eval-visibility";
import { MetricSparkline } from "@/features/evaluations/MetricSparkline";
import { MetricStatusBadge } from "@/features/evaluations/MetricStatusBadge";

export const Route = createFileRoute("/evaluations/dashboard")({
  component: DashboardPage,
});

function DashboardPage() {
  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });

  // 6 meses para sparkline + comparativo (current = último, previous = penúltimo).
  const periods = useMemo(() => buildLastNMonths(6), []);
  const from = periods[0]!;
  const to = periods[periods.length - 1]!;
  const current = to;
  const previous = periods[periods.length - 2]!;

  const defsQ = useQuery<{ definitions: KpiDefinition[] }>({
    queryKey: ["metric-definitions"],
    queryFn: () => api.get("/kpi-definitions"),
  });

  const valuesQ = useQuery<{
    values: Array<{ value: KpiValue; definition: KpiDefinition }>;
  }>({
    queryKey: ["metric-values", "dashboard", from, to],
    queryFn: () => api.get(`/kpi-values?periodFrom=${from}&periodTo=${to}&projectId=`),
  });

  const visibleDefs = useMemo(() => {
    if (!me.data || !defsQ.data) return [];
    return defsQ.data.definitions
      .filter((d) => d.level !== "project")
      .filter((d) => canSeeDef(d.visibility, me.data.role));
  }, [me.data, defsQ.data]);

  const rowsByDef = useMemo(() => {
    const m = new Map<string, KpiValue[]>();
    for (const row of valuesQ.data?.values ?? []) {
      const list = m.get(row.value.definitionId) ?? [];
      list.push(row.value);
      m.set(row.value.definitionId, list);
    }
    return m;
  }, [valuesQ.data]);

  // Filtrar a solo definiciones con status del período actual = red o yellow.
  const flagged = useMemo(() => {
    const out: Array<{
      def: KpiDefinition;
      currentValue: number | null;
      previousValue: number | null;
      status: "bad" | "improvable";
      points: { period: string; value: number | null }[];
    }> = [];

    for (const def of visibleDefs) {
      const rows = rowsByDef.get(def.id) ?? [];
      const cur = rows.find((r) => r.period === current)?.value ?? null;
      const prev = rows.find((r) => r.period === previous)?.value ?? null;
      const status = computeStatus(cur, def);
      if (status !== "bad" && status !== "improvable") continue;
      const points = periods.map((p) => ({
        period: p,
        value: rows.find((r) => r.period === p)?.value ?? null,
      }));
      out.push({ def, currentValue: cur, previousValue: prev, status, points });
    }

    // Rojos primero.
    out.sort((a, b) => (a.status === "bad" ? -1 : 1) - (b.status === "bad" ? -1 : 1));
    return out;
  }, [visibleDefs, rowsByDef, current, previous, periods]);

  const reds = flagged.filter((f) => f.status === "bad");
  const yellows = flagged.filter((f) => f.status === "improvable");

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="evaluaciones"
        title="Dashboard ejecutivo"
        subtitle={`Métricas en alerta del período ${current}. Comparativo vs ${previous} con sparkline de los últimos 6 meses.`}
        actions={
          <Link to="/evaluations" className="btn btn-ghost btn-sm">
            <ArrowLeft className="size-4" /> Volver
          </Link>
        }
      />

      <EvaluationsTabs current="dashboard" role={me.data?.role ?? "member"} />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <SummaryCard
          icon={<AlertOctagon className="size-5" />}
          label="En rojo"
          count={reds.length}
          color="text-error"
        />
        <SummaryCard
          icon={<AlertTriangle className="size-5" />}
          label="En amarillo"
          count={yellows.length}
          color="text-warning"
        />
        <SummaryCard
          icon={<CheckCircle2 className="size-5" />}
          label="OK + sin dato"
          count={visibleDefs.length - flagged.length}
          color="text-success"
        />
      </div>

      {(defsQ.isLoading || valuesQ.isLoading) && (
        <div className="grid place-items-center py-12">
          <span className="loading loading-spinner loading-md text-primary" />
        </div>
      )}

      {!defsQ.isLoading && flagged.length === 0 && (
        <div className="card-elevated py-16 text-center">
          <CheckCircle2 className="size-12 text-success/60 mx-auto mb-3" />
          <h3 className="font-display font-semibold text-base-content mb-1">
            Sin alertas este período
          </h3>
          <p className="text-sm text-base-content/60">
            Ninguna métrica visible está en rojo o amarillo.
          </p>
        </div>
      )}

      {flagged.length > 0 && (
        <ul className="space-y-2">
          {flagged.map((f) => (
            <li key={f.def.id} className="card-elevated p-4 flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className={`badge badge-sm ${f.status === "bad" ? "badge-error" : "badge-warning"}`}
                  >
                    {f.status === "bad" ? "Rojo" : "Amarillo"}
                  </span>
                  <span className="font-medium">{f.def.name}</span>
                </div>
                {f.def.description && (
                  <p className="text-xs text-base-content/55 truncate">{f.def.description}</p>
                )}
              </div>
              <MetricSparkline points={f.points} status={f.status} />
              <MetricStatusBadge def={f.def} current={f.currentValue} previous={f.previousValue} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  count,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
}) {
  return (
    <div className="card-elevated p-4 flex items-center gap-3">
      <div className={`size-10 rounded-lg bg-base-200/40 grid place-items-center ${color}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-base-content/55">{label}</div>
        <div className={`text-2xl font-display font-semibold ${color}`}>{count}</div>
      </div>
    </div>
  );
}

function buildLastNMonths(n: number): string[] {
  const months: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getUTCFullYear(), now.getUTCMonth() - i, 1);
    months.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return months;
}
